import { useCallback, useEffect, useMemo, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import {
  activateLeague,
  addLeagueMembers,
  createLeague,
  getLeagueDetail,
  getLeagues,
  getLeagueStandings,
  publishLeagueRules,
  recordLeagueResult,
  refundLeagueEnrollment,
  removeLeagueMember,
  updateLeague,
} from "../apicalls/leagues";
import { getCourts, searchRegisteredPlayers } from "../apicalls/matchResults";
import Autocomplete from "react-google-autocomplete";
import { googleApiKey } from "../../constAPI";
import "./style/Leagues.css";

const emptyLeagueForm = () => ({
  name: "",
  skill_band: "4.5",
  gender: "men",
  start_date: new Date().toISOString().slice(0, 10),
  deadline: "",
  end_date: "",
  location: "",
  venue_id: "",
  band_low: "4.0",
  band_high: "4.5",
  cost_dollars: "45.00",
  total_players_allowed: "16",
  requires_verified_level: false,
});

const emptyRulesForm = () => ({
  version: "",
  content: "",
});

const emptyResultForm = () => ({
  matchId: "",
  reported_by: "",
  venue_id: "",
  played_at: new Date().toISOString().slice(0, 10),
  format: "bo3",
  retired: false,
  retiredWinner: "you",
  sets: [
    { kind: "set", you: "", opp: "" },
    { kind: "set", you: "", opp: "" },
  ],
});

const normalizeRows = (rows) => Array.isArray(rows) ? rows : [];

const playerId = (player) => String(player.user_id || player.id || "");

const playerLabel = (player) => {
  const name = player.full_name || player.name || "Unknown Name";
  const email = player.email || "No Email";
  const phone = player.phone || "No Phone";
  
  return `${name} - ${email} - ${phone}`;
};


const fixturePlayerName = (fixture, key) => (
  fixture[`${key}_name`] || fixture[key] || "Player"
);

const toDate = (value) => value ? String(value).slice(0, 10) : "";

const centsToDollars = (value) => {
  const cents = Number(value || 0);
  return (cents / 100).toFixed(2);
};

const dollarsToCents = (value) => {
  const normalized = Number(value || 0);
  if (!Number.isFinite(normalized)) return 0;
  return Math.round(normalized * 100);
};

const buildLeaguePayload = (form) => ({
  name: form.name,
  skill_band: form.skill_band,
  gender: form.gender,
  start_date: form.start_date,
  deadline: form.deadline,
  end_date: form.end_date || null,
  location: form.location || null,
  venue_id: form.venue_id || null,
  band_low: form.band_low === "" ? null : Number(form.band_low),
  band_high: form.band_high === "" ? null : Number(form.band_high),
  cost_cents: dollarsToCents(form.cost_dollars),
  total_players_allowed: Number(form.total_players_allowed),
  requires_verified_level: Boolean(form.requires_verified_level),
});

const leagueToForm = (league) => ({
  name: league?.name || "",
  skill_band: league?.skill_band || "",
  gender: league?.gender || "men",
  start_date: toDate(league?.start_date),
  deadline: toDate(league?.deadline),
  end_date: toDate(league?.end_date),
  location: league?.location || "",
  venue_id: league?.venue_id == null ? "" : String(league.venue_id),
  band_low: league?.band_low == null ? "" : String(league.band_low),
  band_high: league?.band_high == null ? "" : String(league.band_high),
  cost_dollars: centsToDollars(league?.cost_cents),
  total_players_allowed: String(league?.total_players_allowed || 16),
  requires_verified_level: Boolean(league?.requires_verified_level),
});

const capacityLabel = (league) => {
  const filled = Number(league?.spots_filled || 0);
  const allowed = Number(league?.total_players_allowed || 0);
  return allowed > 0 ? `${filled}/${allowed} filled` : `${filled} filled`;
};

const courtLabel = (court) => (
  [court.name, court.area].filter(Boolean).join(" · ") || `Court ${court.id}`
);

const googlePlaceLabel = (place) => {
  const formattedAddress = place?.formatted_address || "";
  const name = place?.name || "";

  if (!formattedAddress) return name;
  if (!name || formattedAddress.startsWith(name)) return formattedAddress;
  return [name, formattedAddress].filter(Boolean).join(" ");
};

const leagueLocationLabel = (league) => (
  league?.location ||
  [league?.venue_name, league?.venue_area].filter(Boolean).join(" · ") ||
  "Location TBD"
);

const canRefundMember = (member) => (
  member?.status === "active" &&
  member?.paid &&
  member?.stripe_payment_intent_id &&
  !member?.refund_id
);

const scoreString = (form) => {
  if (form.retired) return "Retired";
  return form.sets
    .filter((set) => set.you !== "" && set.opp !== "")
    .map((set) => set.kind === "mtb" ? `[${set.you}-${set.opp}]` : `${set.you}-${set.opp}`)
    .join("  ");
};

function Leagues() {
  const auth = isAuthenticated();
  const token = auth?.access_token;
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState("");
  const [detail, setDetail] = useState(null);
  const [standings, setStandings] = useState([]);
  const [players, setPlayers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [leagueForm, setLeagueForm] = useState(emptyLeagueForm);
  const [editLeagueForm, setEditLeagueForm] = useState(emptyLeagueForm);
  const [rulesForm, setRulesForm] = useState(emptyRulesForm);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [resultForm, setResultForm] = useState(emptyResultForm);
  const [filters, setFilters] = useState({ status: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingPlayerId, setRemovingPlayerId] = useState("");
  const [refundingMembershipId, setRefundingMembershipId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedLeague = detail?.league || leagues.find((league) => String(league.id) === String(selectedLeagueId));
  const fixtures = normalizeRows(detail?.fixtures);
  const memberships = normalizeRows(detail?.memberships);

  const loadLeagues = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await getLeagues(filters, token);
      const rows = normalizeRows(data.leagues);
      setLeagues(rows);
      if (!selectedLeagueId && rows[0]) setSelectedLeagueId(String(rows[0].id));
    } catch (err) {
      setError(err.message || "Failed to load leagues");
    } finally {
      setLoading(false);
    }
  }, [filters, selectedLeagueId, token]);

  const loadDetail = useCallback(async () => {
    if (!token || !selectedLeagueId) return;
    setError("");
    try {
      const [detailData, standingsData] = await Promise.all([
        getLeagueDetail(selectedLeagueId, token),
        getLeagueStandings(selectedLeagueId, token),
      ]);
      setDetail(detailData);
      setStandings(normalizeRows(standingsData.standings));
    } catch (err) {
      setError(err.message || "Failed to load league detail");
    }
  }, [selectedLeagueId, token]);

  const loadPlayers = useCallback(async () => {
    if (!token) return;
    try {
      const data = await searchRegisteredPlayers({ search: memberSearch, perPage: 100 }, token);
      setPlayers(normalizeRows(data.players));
    } catch (err) {
      setError(err.message || "Failed to load players");
    }
  }, [memberSearch, token]);

  useEffect(() => {
    loadLeagues();
  }, [loadLeagues]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (!selectedLeague) return;
    setEditLeagueForm(leagueToForm(selectedLeague));
    setRulesForm({
      version: selectedLeague.current_rules_version || "",
      content: "",
    });
  }, [selectedLeague]);

  useEffect(() => {
    const timer = setTimeout(loadPlayers, 250);
    return () => clearTimeout(timer);
  }, [loadPlayers]);

  useEffect(() => {
    if (!token) return;
    getCourts(token)
      .then((data) => setCourts(normalizeRows(data.courts)))
      .catch((err) => setError(err.message || "Failed to load courts"));
  }, [token]);

  const playerOptions = useMemo(() => {
    const map = new Map();
    players.forEach((player) => map.set(playerId(player), player));
    memberships.forEach((member) => {
      if (!map.has(String(member.player_id))) {
        map.set(String(member.player_id), {
          user_id: member.player_id,
          full_name: member.full_name,
        });
      }
    });
    return Array.from(map.values());
  }, [memberships, players]);

  const currentResultFixture = fixtures.find((fixture) => String(fixture.id) === String(resultForm.matchId));

  const updateLeagueForm = (key, value) => {
    setLeagueForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateEditLeagueForm = (key, value) => {
    setEditLeagueForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyGooglePlaceToLeagueForm = (setForm, place) => {
    const location = googlePlaceLabel(place);
    if (!location) return;

    setForm((prev) => ({
      ...prev,
      location,
      venue_id: "",
    }));
  };

  const updateRulesForm = (key, value) => {
    setRulesForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateResultForm = (key, value) => {
    setResultForm((prev) => ({ ...prev, [key]: value }));
  };

  const setScoreCell = (index, key, value) => {
    setResultForm((prev) => ({
      ...prev,
      sets: prev.sets.map((set, i) => i === index ? { ...set, [key]: value } : set),
    }));
  };

  const addSet = () => {
    setResultForm((prev) => ({
      ...prev,
      sets: prev.sets.length >= 3 ? prev.sets : [...prev.sets, { kind: "set", you: "", opp: "" }],
    }));
  };

  const removeSet = (index) => {
    setResultForm((prev) => ({
      ...prev,
      sets: prev.sets.length <= 1 ? prev.sets : prev.sets.filter((_, i) => i !== index),
    }));
  };

  const submitLeague = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await createLeague(buildLeaguePayload(leagueForm), token);
      setMessage("League created.");
      setLeagueForm(emptyLeagueForm());
      await loadLeagues();
      if (data.league?.id) setSelectedLeagueId(String(data.league.id));
    } catch (err) {
      setError(err.message || "Failed to create league");
    } finally {
      setSaving(false);
    }
  };

  const submitLeagueUpdate = async (event) => {
    event.preventDefault();
    if (!token || !selectedLeagueId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateLeague(selectedLeagueId, buildLeaguePayload(editLeagueForm), token);
      setMessage("League settings updated.");
      await loadDetail();
      await loadLeagues();
    } catch (err) {
      setError(err.message || "Failed to update league");
    } finally {
      setSaving(false);
    }
  };

  const submitRules = async (event) => {
    event.preventDefault();
    if (!token || !selectedLeagueId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await publishLeagueRules(selectedLeagueId, rulesForm, token);
      setMessage("League rules published.");
      setRulesForm(emptyRulesForm());
      await loadDetail();
      await loadLeagues();
    } catch (err) {
      setError(err.message || "Failed to publish rules");
    } finally {
      setSaving(false);
    }
  };

  const submitMembers = async (event) => {
    event.preventDefault();
    if (!token || !selectedLeagueId || selectedMemberIds.length === 0) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await addLeagueMembers(selectedLeagueId, selectedMemberIds.map(Number), token);
      setMessage(`Members added${data.created_matches?.length ? `, ${data.created_matches.length} late fixtures created` : ""}.`);
      setSelectedMemberIds([]);
      await loadDetail();
      await loadLeagues();
    } catch (err) {
      setError(err.message || "Failed to add members");
    } finally {
      setSaving(false);
    }
  };

  const activateSelectedLeague = async () => {
    if (!token || !selectedLeagueId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await activateLeague(selectedLeagueId, token);
      setMessage(`League activated. ${data.created_matches?.length || 0} fixtures created.`);
      await loadDetail();
      await loadLeagues();
    } catch (err) {
      setError(err.message || "Failed to activate league");
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (member) => {
    if (!token || !selectedLeagueId || !member?.player_id) return;
    const confirmed = window.confirm("Remove this player from the league? Scheduled fixtures involving this player will be cancelled.");
    if (!confirmed) return;

    setRemovingPlayerId(String(member.player_id));
    setError("");
    setMessage("");
    try {
      const data = await removeLeagueMember(selectedLeagueId, member.player_id, token);
      setMessage(`Player removed${data.cancelled_matches?.length ? `, ${data.cancelled_matches.length} scheduled fixtures cancelled` : ""}.`);
      await loadDetail();
      await loadLeagues();
    } catch (err) {
      setError(err.message || "Failed to remove member");
    } finally {
      setRemovingPlayerId("");
    }
  };

  const refundMember = async (member) => {
    if (!token || !selectedLeagueId || !member?.id) return;
    const confirmed = window.confirm("Issue a Stripe refund for this league enrollment? The roster entry will stay visible and marked unpaid.");
    if (!confirmed) return;

    setRefundingMembershipId(String(member.id));
    setError("");
    setMessage("");
    try {
      const data = await refundLeagueEnrollment(selectedLeagueId, member.id, token);
      setMessage(data.replayed ? "Refund already existed." : "Refund issued.");
      await loadDetail();
      await loadLeagues();
    } catch (err) {
      setError(err.message || "Failed to refund enrollment");
    } finally {
      setRefundingMembershipId("");
    }
  };

  const startResultForFixture = (fixture) => {
    setResultForm({
      ...emptyResultForm(),
      matchId: String(fixture.id),
      reported_by: String(fixture.player1_id),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildResultPayload = () => ({
    reported_by: Number(resultForm.reported_by),
    venue_id: Number(resultForm.venue_id),
    played_at: resultForm.played_at,
    format: resultForm.format,
    retired: resultForm.retired ? { winner: resultForm.retiredWinner } : false,
    sets: resultForm.retired ? undefined : resultForm.sets
      .filter((set) => set.you !== "" && set.opp !== "")
      .map((set) => ({ kind: set.kind, you: Number(set.you), opp: Number(set.opp) })),
    score_string: scoreString(resultForm),
  });

  const submitResult = async (event) => {
    event.preventDefault();
    if (!token || !selectedLeagueId || !resultForm.matchId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await recordLeagueResult(selectedLeagueId, resultForm.matchId, buildResultPayload(), token);
      setMessage("League result recorded.");
      setResultForm(emptyResultForm());
      await loadDetail();
    } catch (err) {
      setError(err.message || "Failed to record league result");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="leagues-page">
      <div className="coaches_title leagues-header">
        <div>
          <h1>Leagues</h1>
          <p>Create flex leagues, manage rosters, generate fixtures, and record results.</p>
        </div>
        <select value={filters.status} onChange={(e) => setFilters({ status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {message ? <div className="leagues-alert success">{message}</div> : null}
      {error ? <div className="leagues-alert error">{error}</div> : null}

      <section className="leagues-layout">
        <div className="leagues-card">
          <h2>Create League</h2>
          <form className="leagues-form" onSubmit={submitLeague}>
            <label>
              Name
              <input value={leagueForm.name} onChange={(e) => updateLeagueForm("name", e.target.value)} placeholder="Summer 4.5 Flex League" required />
            </label>
            <div className="leagues-grid">
              <label>
                Skill band
                <input value={leagueForm.skill_band} onChange={(e) => updateLeagueForm("skill_band", e.target.value)} required />
              </label>
              <label>
                Gender
                <select value={leagueForm.gender} onChange={(e) => updateLeagueForm("gender", e.target.value)}>
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                  <option value="mixed">Mixed</option>
                </select>
              </label>
            </div>
            <div className="leagues-grid">
              <label>
                Start date
                <input type="date" value={leagueForm.start_date} onChange={(e) => updateLeagueForm("start_date", e.target.value)} required />
              </label>
              <label>
                Deadline
                <input type="date" value={leagueForm.deadline} onChange={(e) => updateLeagueForm("deadline", e.target.value)} required />
              </label>
            </div>
            <div className="leagues-grid three">
              <label>
                End date
                <input type="date" value={leagueForm.end_date} onChange={(e) => updateLeagueForm("end_date", e.target.value)} />
              </label>
              <label>
                Cost
                <input type="number" min="0" step="0.01" value={leagueForm.cost_dollars} onChange={(e) => updateLeagueForm("cost_dollars", e.target.value)} required />
              </label>
              <label>
                Capacity
                <input type="number" min="1" value={leagueForm.total_players_allowed} onChange={(e) => updateLeagueForm("total_players_allowed", e.target.value)} required />
              </label>
            </div>
            <div className="leagues-grid">
              <label>
                Venue
                <select value={leagueForm.venue_id} onChange={(e) => updateLeagueForm("venue_id", e.target.value)}>
                  <option value="">No venue selected</option>
                  {courts.map((court) => (
                    <option key={court.id} value={court.id}>{courtLabel(court)}</option>
                  ))}
                </select>
              </label>
              <label>
                Search Google location
                <Autocomplete
                  apiKey={googleApiKey}
                  value={leagueForm.location}
                  onChange={(e) => updateLeagueForm("location", e.target.value)}
                  onPlaceSelected={(place) => applyGooglePlaceToLeagueForm(setLeagueForm, place)}
                  options={{
                    types: ["geocode", "establishment"],
                    fields: ["name", "formatted_address", "address_components", "geometry"],
                  }}
                  placeholder="Search courts, parks, clubs..."
                />
              </label>
              <label>
                Location label
                <input value={leagueForm.location} onChange={(e) => updateLeagueForm("location", e.target.value)} placeholder="Penmar Tennis Courts, Santa Monica" />
              </label>
            </div>
            <div className="leagues-grid three">
              <label>
                Band low
                <input type="number" min="1" max="7" step="0.5" value={leagueForm.band_low} onChange={(e) => updateLeagueForm("band_low", e.target.value)} required />
              </label>
              <label>
                Band high
                <input type="number" min="1" max="7" step="0.5" value={leagueForm.band_high} onChange={(e) => updateLeagueForm("band_high", e.target.value)} required />
              </label>
              <label className="leagues-checkbox compact">
                <input type="checkbox" checked={leagueForm.requires_verified_level} onChange={(e) => updateLeagueForm("requires_verified_level", e.target.checked)} />
                Verified level required
              </label>
            </div>
            <button type="submit" disabled={saving}>{saving ? "Saving..." : "Create League"}</button>
          </form>
        </div>

        <div className="leagues-card">
          <h2>League List</h2>
          <div className="leagues-list">
            {loading ? <p>Loading leagues...</p> : null}
            {leagues.map((league) => (
              <button
                key={league.id}
                type="button"
                className={String(league.id) === String(selectedLeagueId) ? "selected" : ""}
                onClick={() => setSelectedLeagueId(String(league.id))}
              >
                <strong>{league.name}</strong>
                <span>{league.skill_band} · {league.gender} · {league.status} · {capacityLabel(league)} · {leagueLocationLabel(league)}</span>
              </button>
            ))}
            {!loading && leagues.length === 0 ? <p>No leagues found.</p> : null}
          </div>
        </div>
      </section>

      {selectedLeague ? (
        <>
          <section className="leagues-card leagues-detail">
            <div className="leagues-detail-head">
              <div>
                <h2>{selectedLeague.name}</h2>
                <p>
                  {selectedLeague.skill_band} · {selectedLeague.gender} · {toDate(selectedLeague.start_date)}
                  {" "}to {toDate(selectedLeague.end_date || selectedLeague.deadline)}
                </p>
              </div>
              <span className={`league-status ${selectedLeague.status}`}>{selectedLeague.status}</span>
            </div>
            <div className="league-metrics">
              <span>{capacityLabel(selectedLeague)}</span>
              <span>${centsToDollars(selectedLeague.cost_cents)} USD</span>
              <span>NTRP {selectedLeague.band_low || "?"}-{selectedLeague.band_high || "?"}</span>
              <span>{leagueLocationLabel(selectedLeague)}</span>
              <span>Rules {selectedLeague.current_rules_version || "not published"}</span>
              {selectedLeague.requires_verified_level ? <span>Verified level required</span> : null}
            </div>
            {!selectedLeague.current_rules_version ? (
              <div className="leagues-alert">
                Publish league rules before players can see this league in Available leagues.
              </div>
            ) : null}
            <div className="leagues-actions">
              <button type="button" onClick={activateSelectedLeague} disabled={saving || selectedLeague.status === "active"}>
                Activate & Generate Fixtures
              </button>
              <button type="button" onClick={loadDetail} disabled={saving}>Refresh</button>
            </div>
          </section>

          <section className="leagues-layout">
            <div className="leagues-card">
              <h2>Edit League</h2>
              <form className="leagues-form" onSubmit={submitLeagueUpdate}>
                <label>
                  Name
                  <input value={editLeagueForm.name} onChange={(e) => updateEditLeagueForm("name", e.target.value)} required />
                </label>
                <div className="leagues-grid">
                  <label>
                    Skill band label
                    <input value={editLeagueForm.skill_band} onChange={(e) => updateEditLeagueForm("skill_band", e.target.value)} required />
                  </label>
                  <label>
                    Gender
                    <select value={editLeagueForm.gender} onChange={(e) => updateEditLeagueForm("gender", e.target.value)}>
                      <option value="men">Men</option>
                      <option value="women">Women</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </label>
                </div>
                <div className="leagues-grid three">
                  <label>
                    Start date
                    <input type="date" value={editLeagueForm.start_date} onChange={(e) => updateEditLeagueForm("start_date", e.target.value)} required />
                  </label>
                  <label>
                    Join deadline
                    <input type="date" value={editLeagueForm.deadline} onChange={(e) => updateEditLeagueForm("deadline", e.target.value)} required />
                  </label>
                  <label>
                    End date
                    <input type="date" value={editLeagueForm.end_date} onChange={(e) => updateEditLeagueForm("end_date", e.target.value)} />
                  </label>
                </div>
                <div className="leagues-grid">
                  <label>
                    Venue
                    <select value={editLeagueForm.venue_id} onChange={(e) => updateEditLeagueForm("venue_id", e.target.value)}>
                      <option value="">No venue selected</option>
                      {courts.map((court) => (
                        <option key={court.id} value={court.id}>{courtLabel(court)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Search Google location
                    <Autocomplete
                      apiKey={googleApiKey}
                      value={editLeagueForm.location}
                      onChange={(e) => updateEditLeagueForm("location", e.target.value)}
                      onPlaceSelected={(place) => applyGooglePlaceToLeagueForm(setEditLeagueForm, place)}
                      options={{
                        types: ["geocode", "establishment"],
                        fields: ["name", "formatted_address", "address_components", "geometry"],
                      }}
                      placeholder="Search courts, parks, clubs..."
                    />
                  </label>
                  <label>
                    Location label
                    <input value={editLeagueForm.location} onChange={(e) => updateEditLeagueForm("location", e.target.value)} placeholder="Penmar Tennis Courts, Santa Monica" />
                  </label>
                </div>
                <div className="leagues-grid three">
                  <label>
                    Cost
                    <input type="number" min="0" step="0.01" value={editLeagueForm.cost_dollars} onChange={(e) => updateEditLeagueForm("cost_dollars", e.target.value)} required />
                  </label>
                  <label>
                    Capacity
                    <input type="number" min="1" value={editLeagueForm.total_players_allowed} onChange={(e) => updateEditLeagueForm("total_players_allowed", e.target.value)} required />
                  </label>
                  <label className="leagues-checkbox compact">
                    <input type="checkbox" checked={editLeagueForm.requires_verified_level} onChange={(e) => updateEditLeagueForm("requires_verified_level", e.target.checked)} />
                    Verified level required
                  </label>
                </div>
                <div className="leagues-grid">
                  <label>
                    Band low
                    <input type="number" min="1" max="7" step="0.5" value={editLeagueForm.band_low} onChange={(e) => updateEditLeagueForm("band_low", e.target.value)} required />
                  </label>
                  <label>
                    Band high
                    <input type="number" min="1" max="7" step="0.5" value={editLeagueForm.band_high} onChange={(e) => updateEditLeagueForm("band_high", e.target.value)} required />
                  </label>
                </div>
                <button type="submit" disabled={saving}>{saving ? "Saving..." : "Save League"}</button>
              </form>
            </div>

            <div className="leagues-card">
              <h2>Publish Rules</h2>
              <form className="leagues-form" onSubmit={submitRules}>
                <label>
                  Version
                  <input value={rulesForm.version} onChange={(e) => updateRulesForm("version", e.target.value)} placeholder="2026-summer-v1" required />
                </label>
                <label>
                  Rules content
                  <textarea rows="9" value={rulesForm.content} onChange={(e) => updateRulesForm("content", e.target.value)} placeholder="League rules, waiver text, code of conduct, and refund policy" required />
                </label>
                <button type="submit" disabled={saving}>{saving ? "Publishing..." : "Publish Rules Version"}</button>
              </form>
            </div>
          </section>

          <section className="leagues-layout">
            <div className="leagues-card">
              <h2>Add Members</h2>
              <form className="leagues-form" onSubmit={submitMembers}>
                <label>
                  Search registered players
                  <input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Name or email" />
                </label>
                <label>
                  Select players
                  <select
                    multiple
                    value={selectedMemberIds}
                    onChange={(e) => setSelectedMemberIds(Array.from(e.target.selectedOptions).map((option) => option.value))}
                  >
                    {playerOptions.map((player) => (
                      <option key={playerId(player)} value={playerId(player)}>{playerLabel(player)}</option>
                    ))}
                  </select>
                </label>
                <button type="submit" disabled={saving || selectedMemberIds.length === 0}>Add Selected</button>
              </form>
            </div>

            <div className="leagues-card">
              <h2>Roster</h2>
              <div className="table-responsive">
                <table className="table leagues-table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Status</th>
                      <th>Joined via</th>
                      <th>Paid</th>
                      <th>Payment</th>
                      <th>Refund</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberships.length ? memberships.map((member) => (
                      <tr key={member.id || member.player_id}>
                        <td>{member.full_name || member.player_id}</td>
                        <td>{member.status}</td>
                        <td>{member.joined_via}</td>
                        <td>{member.paid ? "Yes" : "No"}</td>
                        <td>
                          {member.stripe_payment_intent_id ? (
                            <span className="league-mono">{member.stripe_payment_intent_id}</span>
                          ) : (
                            <span className="league-muted">None</span>
                          )}
                        </td>
                        <td>
                          {member.refund_id ? (
                            <span className="league-muted">
                              {member.refund_status || "refunded"} ${centsToDollars(member.refund_amount_cents)}
                            </span>
                          ) : canRefundMember(member) ? (
                            <button
                              className="league-link warning"
                              type="button"
                              onClick={() => refundMember(member)}
                              disabled={saving || refundingMembershipId === String(member.id)}
                            >
                              {refundingMembershipId === String(member.id) ? "Refunding..." : "Refund"}
                            </button>
                          ) : (
                            <span className="league-muted">—</span>
                          )}
                        </td>
                        <td>
                          {member.status === "active" ? (
                            <button
                              className="league-link danger"
                              type="button"
                              onClick={() => removeMember(member)}
                              disabled={saving || removingPlayerId === String(member.player_id)}
                            >
                              {removingPlayerId === String(member.player_id) ? "Removing..." : "Remove"}
                            </button>
                          ) : (
                            <span className="league-muted">Removed</span>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="7">No members yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="leagues-card">
            <h2>Record League Result</h2>
            <form className="leagues-form" onSubmit={submitResult}>
              <div className="leagues-grid three">
                <label>
                  Fixture
                  <select value={resultForm.matchId} onChange={(e) => updateResultForm("matchId", e.target.value)} required>
                    <option value="">Select fixture</option>
                    {fixtures.map((fixture) => (
                      <option key={fixture.id} value={fixture.id}>
                        #{fixture.match_number} {fixturePlayerName(fixture, "player1")} vs {fixturePlayerName(fixture, "player2")}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Reporter / Player A
                  <select value={resultForm.reported_by} onChange={(e) => updateResultForm("reported_by", e.target.value)} required>
                    <option value="">Select reporter</option>
                    {currentResultFixture ? (
                      <>
                        <option value={currentResultFixture.player1_id}>{fixturePlayerName(currentResultFixture, "player1")}</option>
                        <option value={currentResultFixture.player2_id}>{fixturePlayerName(currentResultFixture, "player2")}</option>
                      </>
                    ) : null}
                  </select>
                </label>
                <label>
                  Venue
                  <select value={resultForm.venue_id} onChange={(e) => updateResultForm("venue_id", e.target.value)} required>
                    <option value="">Select venue</option>
                    {courts.map((court) => (
                      <option key={court.id} value={court.id}>{court.name} {court.area ? `(${court.area})` : ""}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="leagues-grid three">
                <label>
                  Date played
                  <input type="date" value={resultForm.played_at} onChange={(e) => updateResultForm("played_at", e.target.value)} required />
                </label>
                <label>
                  Format
                  <select value={resultForm.format} onChange={(e) => updateResultForm("format", e.target.value)}>
                    <option value="bo3">Best of 3</option>
                    <option value="single">Single set</option>
                  </select>
                </label>
                <label className="leagues-checkbox">
                  <input type="checkbox" checked={resultForm.retired} onChange={(e) => updateResultForm("retired", e.target.checked)} />
                  Match did not finish
                </label>
              </div>
              {resultForm.retired ? (
                <label className="leagues-short">
                  Winner
                  <select value={resultForm.retiredWinner} onChange={(e) => updateResultForm("retiredWinner", e.target.value)}>
                    <option value="you">Reporter</option>
                    <option value="opp">Opponent</option>
                  </select>
                </label>
              ) : (
                <div className="league-scorebox">
                  <div className="league-score-head">
                    <strong>Scores</strong>
                    <button type="button" onClick={addSet} disabled={resultForm.sets.length >= 3}>Add set</button>
                  </div>
                  {resultForm.sets.map((set, index) => (
                    <div className="league-score-row" key={index}>
                      <span>Set {index + 1}</span>
                      <select value={set.kind} onChange={(e) => setScoreCell(index, "kind", e.target.value)}>
                        <option value="set">Set</option>
                        <option value="mtb">Match TB</option>
                      </select>
                      <input type="number" min="0" value={set.you} onChange={(e) => setScoreCell(index, "you", e.target.value)} placeholder="Reporter" required />
                      <input type="number" min="0" value={set.opp} onChange={(e) => setScoreCell(index, "opp", e.target.value)} placeholder="Opponent" required />
                      <button type="button" onClick={() => removeSet(index)} disabled={resultForm.sets.length <= 1}>Remove</button>
                    </div>
                  ))}
                  <small>Preview: {scoreString(resultForm) || "Enter scores"}</small>
                </div>
              )}
              <button type="submit" disabled={saving || !resultForm.matchId}>{saving ? "Saving..." : "Record Result"}</button>
            </form>
          </section>

          <section className="leagues-layout">
            <div className="leagues-card">
              <h2>Fixtures</h2>
              <div className="table-responsive">
                <table className="table leagues-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Players</th>
                      <th>Status</th>
                      <th>Score</th>
                      <th>Winner</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fixtures.length ? fixtures.map((fixture) => (
                      <tr key={fixture.id}>
                        <td>{fixture.match_number}</td>
                        <td>{fixturePlayerName(fixture, "player1")} vs {fixturePlayerName(fixture, "player2")}</td>
                        <td><span className={`league-match-status ${fixture.status}`}>{fixture.status}</span></td>
                        <td>{fixture.score || "—"}</td>
                        <td>{fixture.winner_id || "—"}</td>
                        <td>
                          <button className="league-link" type="button" onClick={() => startResultForFixture(fixture)}>
                            Result
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="6">No fixtures yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="leagues-card">
              <h2>Standings</h2>
              <div className="table-responsive">
                <table className="table leagues-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>W</th>
                      <th>L</th>
                      <th>MP</th>
                      <th>Games</th>
                      <th>Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.length ? standings.map((row) => (
                      <tr key={row.player_id}>
                        <td>{row.rank}</td>
                        <td>{row.full_name || row.player_id}</td>
                        <td>{row.wins}</td>
                        <td>{row.losses}</td>
                        <td>{row.matches_played}</td>
                        <td>{row.games_for}-{row.games_against}</td>
                        <td>{row.game_differential}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="7">No standings yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
}

export default Leagues;
